import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {withStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import SvgIconLayers from '@material-ui/icons/Layers';

import leaflet from 'leaflet';

import ViewStack from './ViewStack';
import ControlButton from './ControlButton';

const styles = (theme) => {
    return {
        button: {
            position: 'absolute',
            right: 0,
            bottom : 0,
            '&$selected': {
                backgroundColor: theme.palette.secondary.main,
            },
            '&$selected:hover:not($disabled)': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
        paper: {
            position: 'relative',
            marginRight: 40,
            borderColor: theme.palette.primary.mainBorder,
            borderWidth: 1,
            borderStyle: 'solid',
            backgroundColor: theme.palette.primary.main,
            visibility: 'hidden',
            zIndex: 2000,
            '&$selected': {
                visibility: 'visible',
            },
        },
        menuItem: {
            position: 'relative',
            '&:hover': {
                backgroundColor: theme.palette.primary.hoverOverlay,
            }
        },
        menuLabel: {
            color: theme.palette.primary.displayText,
            fontSize: 12,
            margin: 0,
            paddingLeft: 8,
            paddingRight: 8,
            paddingTop: 4,
            paddingBottom: 4,
            textAlign: 'left',
            cursor: 'pointer',
            '&$selected': {
                color: theme.palette.primary.selectedDisplayText,
            },
        },
        default: {},
        selected: {},
        disabled: {},
        tooltip: {
            maxWidth: 250,
        },
        backdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.0)',
            zIndex: 1900,
        }
    };
}

class LayerControl extends Component {
    constructor(props) {
        super(props);

        this.state = {
            open: false,
        };

        this._layerEntries = [];
    }

    shouldComponentUpdate = (nextProps, nextState) => {
        const nextView = this.props.viewStack.peek();
        const nextUid = nextView.uid;

        if (this._uid !== nextUid && nextView.layer) {
            this._uid = nextUid;
            this._layerEntries = Object.entries(nextView.layer);
            return true;
        }

        return (this.state.open !== nextState.open);
    }

    render = () => {
        const view = this.props.viewStack.peek();

        if (this.props.viewStack.isDefaultBase()) {
            return null;
        }

        this._uid = view.uid;

        const {classes} = this.props;

        const selectState = (this.state.open) ? classes.selected : classes.default;

        const buttonClasses = {
            disabled: classes.disabled,
        }

        return (
            <div className={this.props.className}>
                <ControlButton
                    tooltip='Analysis layers'
                    tooltipPlacement='left'
                    className={classNames(classes.button, selectState)}
                    classes={buttonClasses}
                    onClick={this._handleClick}
                    disabled={(this._layerEntries.length <= 1)}
                >
                    <SvgIconLayers/>
                </ControlButton>
                {this.state.open ? (
                    <div>
                        <div
                            className={classes.backdrop}
                            onClick={this._handleBackdropClick}
                        />
                        <ClickAwayListener onClickAway={this._handleClose}>
                            <Paper className={classNames(classes.paper, selectState)}>
                                {this._layerEntries.map(this._renderItem)}
                            </Paper>
                        </ClickAwayListener>
                    </div>
                ) : null}
            </div>
        );
    }

    _renderItem = ([type, value], index, arr) => {
        const selected = (type === this.props.viewStack.peek().selectedLayerType)

        const {classes} = this.props;

        const labelState = (selected) ? classes.selected : classes.default;

        const tooltipClasses = {
            tooltip: classes.tooltip,
        };

        return (
            <Tooltip
                classes={tooltipClasses}
                key={index}
                title={value.desc ? value.desc : ''}
                placement='left'
                enterDelay={500}
            >
                <div
                    className={classes.menuItem}
                    key={index}
                    onClick={() => {this._handleChange(type);}}
                    onMouseOver={this._handleMouseOver}
                    onMouseOut={this._handleMouseOut}
                >
                    <Typography className={classNames(classes.menuLabel, labelState)}>
                        {value.name}
                    </Typography>
                </div>
            </Tooltip>
        );
    }

    _handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.setState({
            open: true,
        });
    }

    _handleClose = (e) => {
        e.preventDefault();
        e.stopPropagation();

        this.setState({
            open: false,
        });
    }

    _handleBackdropClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }

    _handleChange = (value) => {
        this.setState({
            open: false,
        });

        this._activate(value);
    }

    _activate = (type) => {
        if (this.props.onChange) {
            this.props.onChange(type);
        }
    }
}

LayerControl.propTypes = {
    viewStack: PropTypes.instanceOf(ViewStack).isRequired,
    map: PropTypes.instanceOf(leaflet.Map).isRequired,
    onChange: PropTypes.func,
}

export default withStyles(styles)(LayerControl);
